import { config } from "../../config/app-config";
import { ROUTES } from "../../routes/paths";
import { profile } from "../../customer";
import { getPreferences } from "../../services/user-preferences";

export interface NavItem {
  path: string;
  label: string;
  category: string;
  isDemoPage?: boolean;
}

export const allNavItems: NavItem[] = [
  { path: ROUTES.PAGE.FOR("jobs"), label: "Jobs", category: "Pages" },
  { path: ROUTES.PAGE.FOR("schedules"), label: "Schedules", category: "Pages" },
  { path: ROUTES.APP.DASHBOARD, label: "Dashboard", category: "Apps" },
  // Both until glen has judged the DSL page's row visibility for a non-admin
  // and ruled on the My / All filter
  // (FEATURE-20260917-retire-hand-wired-pages-replaced-by-page-dsl, phase 2).
  { path: ROUTES.FILESET.LIST, label: "Filesets (hand-wired)", category: "Apps" },
  { path: ROUTES.PAGE.FOR("filesets"), label: "Filesets", category: "Apps" },
  { path: ROUTES.PAGE.FOR("checkpoint-run"), label: "Run Programs", category: "Apps" },
  { path: ROUTES.PAGE.FOR("checkpoint-browser"), label: "Run in Browser", category: "Apps" },
  { path: ROUTES.CHECKPOINT.SAVED, label: "Saved Programs", category: "Apps" },
  { path: ROUTES.PAGE.FOR("program-runs"), label: "Program Runs", category: "Apps" },
  { path: ROUTES.LOOM.CHAT, label: "Loom Chat", category: "Apps" },
  { path: ROUTES.LOOM.BROWSER, label: "Loom Files", category: "Apps" },
  { path: ROUTES.PAGE.FOR("loom-mounts"), label: "Loom Mounts", category: "Apps" },
  { path: ROUTES.PAGE.FOR("loom-usage"), label: "Loom Usage", category: "Apps" },
  { path: ROUTES.PAGE.FOR("monitors"), label: "Monitors", category: "Apps" },
  { path: ROUTES.APP.LOGS, label: "Log Viewer", category: "Apps" },
  { path: ROUTES.APP.SQL_QUERY, label: "SQL Query", category: "Apps" },
  { path: ROUTES.APP.SQL_EXPLAIN, label: "SQL Explain", category: "Apps" },
  { path: ROUTES.APP.RPC_TEST, label: "Invoke", category: "Mesh" },
  { path: ROUTES.APP.PINGER, label: "Mesh Ping", category: "Mesh" },
  { path: ROUTES.APP.REGISTRY, label: "Registry", category: "Apps" },
  { path: ROUTES.PAGE.FOR("workers"), label: "Workers", category: "Apps" },
  { path: ROUTES.APP.PROFILE, label: "Profile", category: "Apps" },
  { path: ROUTES.APP.WHOAMI, label: "Who Am I", category: "Apps" },
  { path: ROUTES.PAGE.FOR("mcp-urls"), label: "MCP URLs", category: "Apps" },
  { path: ROUTES.PAGE.FOR("mcp-auths"), label: "MCP Auths", category: "Apps" },
  {
    path: ROUTES.ADMIN.DIRECTORY,
    label: "User Directory",
    category: "Admin",
  },
  {
    path: ROUTES.ADMIN.PENDING,
    label: "Pending Credentials",
    category: "Admin",
  },
  {
    path: ROUTES.PAGE.FOR("databases"),
    label: "Databases",
    category: "Admin",
  },
  {
    path: ROUTES.PAGE.FOR("secrets"),
    label: "Secrets",
    category: "Admin",
  },
  {
    path: ROUTES.PAGE.FOR("alert-channels"),
    label: "Alert Channels",
    category: "Admin",
  },
  {
    path: ROUTES.PAGE.FOR("firewall"),
    label: "Firewall: Tables",
    category: "Admin",
  },
  {
    path: ROUTES.PAGE.FOR("firewall-global"),
    label: "Firewall: Global Rules",
    category: "Admin",
  },
  {
    path: ROUTES.PAGE.FOR("endpoint-auth"),
    label: "Firewall: Endpoint Grants",
    category: "Admin",
  },
  {
    path: ROUTES.DEMO.PLAYGROUND,
    label: "Demo: Playground",
    category: "Demos",
    isDemoPage: true,
  },
  {
    path: ROUTES.DEMO.FORM,
    label: "Demo: Form",
    category: "Demos",
    isDemoPage: true,
  },
  {
    path: ROUTES.DEMO.PARENT_CHILD,
    label: "Demo: Parent-Child Table",
    category: "Demos",
    isDemoPage: true,
  },
  {
    path: ROUTES.DEMO.TABLE_CHANGE_TRACKING,
    label: "Demo: Table Change Tracking",
    category: "Demos",
    isDemoPage: true,
  },
];

/**
 * The menu (and, through global-commands.ts, the command palette): the
 * customer profile's own entries lead, then the base entries minus the
 * categories and paths the profile hides. Customer-specific entries live on
 * the profile (customers/<id>/customer.ts), never in allNavItems.
 */
export function getVisibleNavItems(): NavItem[] {
  const hideCategories = new Set(profile.nav.hideCategories ?? []);
  const hidePaths = new Set(profile.nav.hidePaths ?? []);
  const showDemoPages = getPreferences().showDemoPages ?? config.app.showDemoPages;
  const base = allNavItems.filter((item) => {
    if (item.isDemoPage && !showDemoPages) return false;
    if (hideCategories.has(item.category) || hidePaths.has(item.path)) return false;
    return true;
  });
  return [...profile.nav.items, ...base];
}

/** the command palette category that leads every other one */
export const FAVORITES_CATEGORY = "Favorites";

/**
 * The user's favorite pages (/app/profile), in the order they chose, labelled
 * from the menu entry for the same path. A favorite whose page is no longer in
 * the menu is dropped rather than shown as a dead link.
 */
export function getFavoriteNavItems(): NavItem[] {
  const byPath = new Map(getVisibleNavItems().map((i) => [i.path, i]));
  return (getPreferences().favoritePaths ?? []).flatMap((path) => {
    const item = byPath.get(path);
    return item ? [{ ...item, category: FAVORITES_CATEGORY }] : [];
  });
}
